import React, { useState } from 'react';
import api, { takeLastErrorRef } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Bug, Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

/**
 * Shared "Probleem melden" button + dialog. Works in BOTH worlds:
 * the manager app and the portal. The backend derives the reporter identity
 * from the session cookie — never from the body.
 *
 * Props:
 *  - locationScope: current location scope string (optional)
 *  - triggerClassName / triggerVariant: style the trigger button
 *  - compact: icon-only trigger (header)
 */
export const ProbleemMeldenDialog = ({
  locationScope = null,
  triggerClassName = '',
  compact = false,
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [omschrijving, setOmschrijving] = useState('');
  const [verwacht, setVerwacht] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setOmschrijving(''); setVerwacht(''); setDone(false); setBusy(false);
  };

  const submit = async () => {
    if (!omschrijving.trim()) return;
    setBusy(true);
    try {
      await api.post('/bug-reports', {
        omschrijving: omschrijving.trim(),
        verwacht: verwacht.trim() || null,
        route: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
        location_scope: locationScope || null,
        correlation_ref: takeLastErrorRef() || null,
      });
      setDone(true);
      toast({ title: 'Bedankt! Je melding is doorgestuurd.' });
    } catch (e) {
      const msg = e?.response?.status === 429
        ? 'Te veel meldingen in korte tijd. Probeer het later opnieuw.'
        : 'Melding versturen mislukt. Probeer het opnieuw.';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="probleem-melden-trigger"
        onClick={() => { reset(); setOpen(true); }}
        title="Probleem melden"
        aria-label="Probleem melden"
        className={triggerClassName || (
          compact
            ? 'relative flex h-9 w-9 items-center justify-center rounded-md text-white/80 hover:bg-white/10 hover:text-white transition-colors'
            : 'inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs text-white transition-colors'
        )}
      >
        <Bug size={compact ? 18 : 14} />
        {!compact && <span>Probleem melden</span>}
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="sm:max-w-md" data-testid="probleem-melden-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-brand-navy">
              <Bug size={18} /> Probleem melden
            </DialogTitle>
            <DialogDescription>
              Loopt er iets niet zoals verwacht? Laat het ons weten — we kijken ernaar.
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="py-8 text-center" data-testid="probleem-melden-success">
              <CheckCircle2 size={40} className="mx-auto mb-3 text-emerald-500" />
              <p className="text-sm font-medium text-slate-700">Bedankt voor je melding!</p>
              <p className="mt-1 text-xs text-slate-500">We hebben je bericht ontvangen.</p>
              <Button className="mt-5" onClick={() => setOpen(false)} data-testid="probleem-melden-close">
                Sluiten
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pm-omschrijving">Wat deed je? <span className="text-rose-500">*</span></Label>
                <Textarea
                  id="pm-omschrijving"
                  data-testid="probleem-melden-omschrijving"
                  value={omschrijving}
                  onChange={(e) => setOmschrijving(e.target.value)}
                  maxLength={2000}
                  rows={4}
                  placeholder="Beschrijf welke stap je nam en wat er gebeurde…"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pm-verwacht">Wat verwachtte je? <span className="text-slate-400 text-xs">(optioneel)</span></Label>
                <Textarea
                  id="pm-verwacht"
                  data-testid="probleem-melden-verwacht"
                  value={verwacht}
                  onChange={(e) => setVerwacht(e.target.value)}
                  maxLength={2000}
                  rows={2}
                  placeholder="Wat had er volgens jou moeten gebeuren?"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                  Annuleren
                </Button>
                <Button
                  onClick={submit}
                  disabled={busy || !omschrijving.trim()}
                  data-testid="probleem-melden-submit"
                  className="bg-brand-navy hover:bg-brand-navy/90"
                >
                  {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                  Versturen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProbleemMeldenDialog;
